import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { HotKeyBox } from '@modules/common/components/HotKeyIcons/HotKeyBox';
import { OptionAltHotkeySymbol } from '@modules/common/components/OptionAltHotkeySymbol';
import { useHotkey } from '@modules/common/hooks';
import { KeyboardReturnRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Button, Stack, Typography } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';

interface ManageMetadataEditDrawerFooterProps {
  onCancel: () => void;
  onSave: () => void;
  isSaveRequestInProgress: boolean;
  isSaveDisabled?: boolean;
}

export const ManageMetadataEditDrawerFooter = ({
  onCancel,
  onSave,
  isSaveRequestInProgress,
  isSaveDisabled,
}: ManageMetadataEditDrawerFooterProps) => {
  useHotkey({
    hotkey: 'Alt+Enter',
    onHotkey: onSave,
    isHotKeyDisabled: isSaveDisabled || isSaveRequestInProgress,
    actionTitle: 'Update Reason',
  });

  const isAuthorized = useIsAuthorized({
    resource: PermissionResource.Metadata,
    operation: PermissionOperation.Write,
  });

  const isDisabled = isSaveDisabled || !isAuthorized;

  return (
    <Stack
      direction="row"
      px={3}
      py={2}
      gap={1}
      mt="auto"
      alignItems="center"
      width="40vw"
      justifyContent="flex-end"
      sx={{
        borderTop: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        bottom: 0,
        backgroundColor: 'misc.drawer.background',
        width: '100%',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Button variant="outlined" onClick={onCancel} endIcon={<EscHotKeyIcon />}>
        Cancel
      </Button>
      <LoadingButton
        variant="contained"
        loading={isSaveRequestInProgress}
        onClick={onSave}
        endIcon={
          !isSaveRequestInProgress && (
            <HotKeyBox
              hotkeyDisplay={
                <Typography variant="subtitle2">
                  <OptionAltHotkeySymbol />
                  <KeyboardReturnRounded fontSize="small" />
                </Typography>
              }
              isDisabled={isDisabled}
            />
          )
        }
        disabled={isDisabled}
      >
        Save Changes
      </LoadingButton>
    </Stack>
  );
};
